import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  color: #FECE1B;
  margin-bottom: 2rem;
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;

  h2 {
    color: #1a1a1a;
    margin-bottom: 1rem;
    font-size: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h3 {
    color: #333;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
    font-size: 1.25rem;

    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }

  p {
    margin-bottom: 1rem;
    color: #555;
  }

  ul, ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      color: #555;
    }
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-top: 0;
    color: #FECE1B;
  }

  p {
    margin-bottom: 0.5rem;
  }
`;

const TermsConditionsPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Terms and conditions for ZAN RESIN SURFACES LTD - Professional resin bound surfacing services across London, Surrey, Hertfordshire, Kent, Essex, Buckinghamshire and Berkshire." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/terms-conditions/" />
      </Helmet>

      <PageContainer>
        <Title>Terms & Conditions</Title>

        <Section>
          <h2>1. Company Information</h2>
          <p>
            These terms and conditions are governed by ZAN RESIN SURFACES LTD, a company registered in England and Wales.
          </p>
          <ul>
            <li><strong>Company Name:</strong> ZAN RESIN SURFACES LTD</li>
            <li><strong>Company Number:</strong> 16698534</li>
            <li><strong>Registered Office:</strong> Broadfield Broadhurst Gardens, London, England, NW6 3BN</li>
            <li><strong>Email:</strong> info@resinsurfaceslondon.co.uk</li>
          </ul>
        </Section>

        <Section>
          <h2>2. Acceptance of Terms</h2>
          <p>
            By accessing and using this website or engaging our services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </p>
        </Section>

        <Section>
          <h2>3. Services Provided</h2>
          <p>
            ZAN RESIN SURFACES LTD provides professional resin bound surfacing services including:
          </p>
          <ul>
            <li>Resin bound driveways</li>
            <li>Resin bound pathways</li>
            <li>Resin bound pool decks</li>
            <li>Resin bound patios</li>
            <li>Commercial resin surfacing</li>
          </ul>
          <p>
            We operate primarily across London, Surrey, and Hertfordshire, with services extending to surrounding areas as agreed.
          </p>
        </Section>

        <Section>
          <h2>4. Quotations and Pricing</h2>
          <h3>4.1 Free Quotations</h3>
          <p>
            All quotations provided are free of charge and valid for 30 days from the date of issue, unless otherwise stated.
          </p>
          
          <h3>4.2 Pricing</h3>
          <p>
            All prices quoted are exclusive of VAT unless otherwise stated. Prices may vary based on:
          </p>
          <ul>
            <li>Site conditions and accessibility</li>
            <li>Preparation work required</li>
            <li>Materials and aggregates selected</li>
            <li>Project complexity and size</li>
          </ul>
        </Section>

        <Section>
          <h2>5. Booking and Deposits</h2>
          <h3>5.1 Booking Process</h3>
          <p>
            A booking is confirmed once a signed quotation is returned and a deposit is received.
          </p>
          
          <h3>5.2 Deposits</h3>
          <p>
            A deposit of 25% of the total project cost is required to secure your booking and order materials. This deposit is non-refundable if the project is cancelled within 7 days of the scheduled start date.
          </p>
        </Section>

        <Section>
          <h2>6. Installation and Completion</h2>
          <h3>6.1 Site Preparation</h3>
          <p>
            The customer is responsible for ensuring the site is accessible and any necessary groundwork is completed as agreed. Additional charges may apply for extra preparation work required.
          </p>
          
          <h3>6.2 Weather Conditions</h3>
          <p>
            Installation may be postponed due to adverse weather conditions. We will reschedule at the earliest suitable opportunity at no additional cost.
          </p>
          
          <h3>6.3 Completion and Payment</h3>
          <p>
            Full payment is due upon completion of the work. Payment can be made by bank transfer, cheque, or cash as agreed.
          </p>
        </Section>

        <Section>
          <h2>7. Warranties and Guarantees</h2>
          <h3>7.1 Workmanship Guarantee</h3>
          <p>
            We provide a 10-year guarantee on workmanship for all resin bound installations, subject to proper maintenance and normal wear and tear.
          </p>
          
          <h3>7.2 Material Guarantees</h3>
          <p>
            Material guarantees are provided by the manufacturer and vary by product. Details will be provided with your quotation.
          </p>
        </Section>

        <Section>
          <h2>8. Customer Responsibilities</h2>
          <p>
            Customers are responsible for:
          </p>
          <ul>
            <li>Ensuring site access is available for the duration of the work</li>
            <li>Protecting the installation from damage during the curing period</li>
            <li>Regular maintenance as advised</li>
            <li>Notifying us of any issues within 48 hours of completion</li>
          </ul>
        </Section>

        <Section>
          <h2>9. Limitation of Liability</h2>
          <p>
            Our liability is limited to the value of the work carried out. We are not liable for any indirect or consequential losses.
          </p>
        </Section>

        <Section>
          <h2>10. Force Majeure</h2>
          <p>
            We shall not be liable for any delay or failure to perform due to circumstances beyond our reasonable control, including but not limited to acts of God, war, terrorism, or government restrictions.
          </p>
        </Section>

        <Section>
          <h2>11. Dispute Resolution</h2>
          <p>
            Any disputes will be resolved through mediation where possible. If mediation fails, disputes will be subject to the jurisdiction of the English courts.
          </p>
        </Section>

        <Section>
          <h2>12. Changes to Terms</h2>
          <p>
            We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the new terms.
          </p>
        </Section>

        <ContactInfo>
          <h3>Contact Information</h3>
          <p><strong>ZAN RESIN SURFACES LTD</strong></p>
          <p>Company Number: 16698534</p>
          <p>Registered Office: Broadfield Broadhurst Gardens, London, England, NW6 3BN</p>
          <p>Email: <a href="mailto:info@resinsurfaceslondon.co.uk">info@resinsurfaceslondon.co.uk</a></p>
          <p>Phone: <a href="tel:+447535935779">07535 935 779</a></p>
          <p><em>Last updated: January 2025</em></p>
        </ContactInfo>
      </PageContainer>
    </>
  );
};

export default TermsConditionsPage;
